import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
    Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
    FormHelperText, Grid, InputLabel, MenuItem, Select, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowBack, Download } from "@mui/icons-material";
import { baseData, getData } from "../../services/base.service";
import { downloadBlob } from "../../services/projectFiles.service";
import { useNotification } from "../Shared/NotificationProvider";
import { GizDay } from "../../types/billingTypes";
import { rfsDisplay } from "./timesheetUtils";
import GizAggregationHelp from "./GizAggregationHelp";

type GizOptions = {
    projects: {
        id: number; projectNumber: string | null; projectName: string | null; client: string | null; rfsTracking: boolean;
        hours: number; hoursWithoutRfs: number; rfs: { id: number; name: string; code: string | null; hours: number }[];
    }[];
};

const NO_RFS: GizOptions['projects'][number]['rfs'] = [];
const formatH =(h: number) => `${Math.round(h * 100) / 100} h`;
const monthLong = (m: string) => new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

// GIZ timesheet straight from the timesheet - independent of the project controlling (no cost plan,
// billing period or stored timesheet needed). Expert = selected timesheet user, default: me.
type TimesheetGizDialogProps = {
    open: boolean;
    onClose: () => void;
    initialProjectId?: number | null;
    initialUserOid?: string | null;
    initialMonth?: string | null;
};

type GizRule = 'pooled' | 'daily';

type TimesheetGizDraft = {
    project: { ID: number; projectNumber: string | null; projectName: string | null; contractNumber: string | null; gizProjectNumber: string | null };
    months: { month: string; hoursBooked: number; days: GizDay[] }[];
    crossMonth: boolean;
    rule: GizRule;
    rfs: { id: number; name: string; code: string | null } | null;
    userOid: string;
    expertName: string;
    role: string;
    roleSource: string | null;
    hoursBooked: number;
};

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const previousMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MAX_MONTHS = 12;

const monthIndex = (m: string) => { const [y, mo] = m.split('-').map(Number); return y * 12 + mo - 1; };
const monthCount = (from: string, to: string) => (from && to ? monthIndex(to) - monthIndex(from) + 1 : 0);
const monthShort = (m: string) => new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const dayTotals = (days: GizDay[]) => days.reduce((sum, d) => ({
    domestic: sum.domestic + (d.expertDays && !d.inCountry ? d.expertDays : 0),
    abroad: sum.abroad + (d.expertDays && d.inCountry ? d.expertDays : 0),
    perDiem: sum.perDiem + (d.perDiem ? 1 : 0),
    accommodation: sum.accommodation + (d.accommodation ? 1 : 0),
}), { domestic: 0, abroad: 0, perDiem: 0, accommodation: 0 });

const weekday = (month: string, day: number) => {
    const [y, m] = month.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, day)).getUTCDay();
};

function TimesheetGizDialog({ open, onClose, initialProjectId, initialUserOid, initialMonth }: TimesheetGizDialogProps) {
    const theme = useTheme();
    const notify = useNotification();
    const { accounts } = useMsal();
    const me = accounts[0];
    const [step, setStep] = useState<'select' | 'review'>('select');
    const [options, setOptions] = useState<GizOptions | null>(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [users, setUsers] = useState<{ oid: string; name: string }[]>([]);
    const [projectId, setProjectId] = useState<number | null>(null);
    const [userOid, setUserOid] = useState('');
    const [monthFrom, setMonthFrom] = useState(previousMonth());
    const [monthTo, setMonthTo] = useState(previousMonth());
    const [includeRfsCode, setIncludeRfsCode] = useState(true);
    const [crossMonth, setCrossMonth] = useState(false);
    const [multiMonth, setMultiMonth] = useState(false); // off = one month only
    const [tab, setTab] = useState(0);
    // '' = all hours on the project; otherwise one timesheet for this RfS
    const [rfsId, setRfsId] = useState<number | ''>('');
    const [rule, setRule] = useState<GizRule>('pooled');
    const [draft, setDraft] = useState<TimesheetGizDraft | null>(null);
    const [monthsData, setMonthsData] = useState<{ month: string; hoursBooked: number; days: GizDay[] }[]>([]);
    const [expertName, setExpertName] = useState('');
    const [role, setRole] = useState('');
    const [bulkLocation, setBulkLocation] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStep('select');
        setDraft(null);
        setProjectId(initialProjectId ?? null);
        setUserOid(initialUserOid || me?.localAccountId || '');
        setMonthFrom(initialMonth ?? previousMonth());
        setMonthTo(initialMonth ?? previousMonth());
        setIncludeRfsCode(true);
        setCrossMonth(false);
        setTab(0);
        setRfsId('');
        setRule('pooled');
        setOptions(null);
        getData('timesheets/report?from=2000-01-01&to=2100-12-31&kind=project').then((res) => setUsers(res.users)).catch(() => setUsers([]));
    }, [open, initialProjectId, initialUserOid, initialMonth, me?.localAccountId]);

    // The signed-in user is always selectable, even without project hours yet.
    const userOptions = me && !users.some((u) => u.oid === me.localAccountId)
        ? [{ oid: me.localAccountId, name: me.name ?? me.username }, ...users] : users;

    const lastMonth = multiMonth ? monthTo : monthFrom;
    const nMonths = monthCount(monthFrom, lastMonth);
    const rangeError = !monthFrom || !monthTo ? null : nMonths < 1 ? '"To month" must not be before "From month".'
        : nMonths > MAX_MONTHS ? `At most ${MAX_MONTHS} months at once.` : null;
    const rangeOk = !!monthFrom && !!lastMonth && !rangeError;

    // Projects / RfS the selected expert actually booked implementation hours on in the selected month(s).
    const projectOptions = options?.projects ?? [];
    const selectedProject = projectOptions.find((p) => p.id === projectId) ?? null;
    const projectRfs = selectedProject?.rfs ?? NO_RFS;
    const selectedRfs = rfsId !== '' ? projectRfs.find((r) => r.id === rfsId) ?? null : null;
    const rfsCode = selectedRfs?.code ?? null;
    const noHours = !!options && !optionsLoading && projectOptions.length === 0;
    const expertLabel = userOptions.find((u) => u.oid === userOid)?.name ?? 'this expert';
    const periodLabel = nMonths > 1 ? `${monthShort(monthFrom)} – ${monthShort(lastMonth)}` : monthLong(monthFrom);

    useEffect(() => {
        if (!open || !userOid || !rangeOk) { setOptions(null); setOptionsLoading(false); return undefined; }
        let stale = false;
        setOptionsLoading(true);
        const params = new URLSearchParams({ userOid, month: monthFrom });
        if (nMonths > 1) params.set('monthTo', lastMonth);
        getData(`timesheets/giz/options?${params}`)
            .then((res) => {
                if (stale) return;
                const result = res as GizOptions;
                setOptions(result);
                setProjectId((prev) => (prev !== null && result.projects.some((p) => p.id === prev) ? prev : result.projects.length === 1 ? result.projects[0].id : null));
            })
            .catch((error) => {
                if (stale) return;
                console.error(error);
                setOptions({ projects: [] });
                setProjectId(null);
                notify(`Couldn't load the booked hours: ${(error as Error).message}`, 'error');
            })
            .finally(() => { if (!stale) setOptionsLoading(false); });
        return () => { stale = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userOid, monthFrom, lastMonth, nMonths, rangeOk]);

    // The RfS choice must exist for the selected project in the result.
    useEffect(() => {
        if (rfsId !== '' && !projectRfs.some((r) => r.id === rfsId)) setRfsId('');
    }, [rfsId, projectRfs]);
    const crossMonthAvailable = nMonths > 1 && rule === 'pooled';
    const rolePreview = includeRfsCode && draft?.rfs?.code ? `${draft.rfs.code} - ${role.trim()}` : role.trim();

    const loadDraft = async (withRule: GizRule = rule) => {
        if (!projectId || !rangeOk) return;
        setBusy(true);
        try {
            const params = new URLSearchParams({ projectId: String(projectId), month: monthFrom, rule: withRule });
            if (nMonths > 1) params.set('monthTo', lastMonth);
            if (nMonths > 1 && withRule === 'pooled' && crossMonth) params.set('crossMonth', 'true');
            if (userOid) params.set('userOid', userOid);
            if (rfsId !== '') params.set('rfsId', String(rfsId));
            const result = await getData(`timesheets/giz/draft?${params}`) as TimesheetGizDraft;
            setDraft(result);
            setRule(result.rule);
            setMonthsData(result.months);
            setTab(0);
            setCrossMonth(!!result.crossMonth);
            setExpertName(result.expertName);
            setRole(result.role);
            setStep('review');
        } catch (error) {
            notify(`Couldn't prepare the timesheet: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const current = monthsData[Math.min(tab, Math.max(monthsData.length - 1, 0))];
    const month = current?.month ?? monthFrom;
    const days = current?.days ?? [];
    const setDays = (fn: (prev: GizDay[]) => GizDay[]) => setMonthsData((prev) => prev.map((m) => (m === current ? { ...m, days: fn(m.days) } : m)));
    const updateDay = (day: number, patch: Partial<GizDay>) => setDays((prev) => prev.map((d) => (d.day === day ? { ...d, ...patch } : d)));
    const workedDays = days.filter((d) => d.expertDays);
    const setForWorked = (patch: Partial<GizDay>) => setDays((prev) => prev.map((d) => (d.expertDays ? { ...d, ...patch } : d)));
    const allChecked = (key: 'inCountry' | 'perDiem' | 'accommodation') => workedDays.length > 0 && workedDays.every((d) => d[key]);

    const totals = dayTotals(days);
    const grand = dayTotals(monthsData.flatMap((m) => m.days));
    const grandHours = monthsData.reduce((sum, m) => sum + m.hoursBooked, 0);

    const download = async () => {
        if (!draft) return;
        if (!expertName.trim() || !role.trim()) { notify('Name and role are required.', 'warning'); return; }
        setBusy(true);
        try {
            const initials = expertName.trim().split(/[\s-]+/).filter(Boolean).map((p) => p[0].toUpperCase()).join('');
            const rfsPart = (draft.rfs?.name ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
            const file = await baseData('timesheets/giz/file', 'POST', {
                projectId: draft.project.ID,
                expertName,
                role,
                rfsId: draft.rfs?.id ?? null,
                includeRfsCode: includeRfsCode && !!draft.rfs?.code,
                months: monthsData.map((m) => ({
                    month: m.month,
                    days: m.days.map((d) => ({
                        day: d.day, expertDays: d.expertDays, inCountry: d.inCountry, perDiem: d.perDiem, accommodation: d.accommodation,
                        location: d.location || null, activity: d.activity || null,
                    })),
                })),
            }, 'blob') as Blob;
            const monthPart = monthsData.length > 1 ? `${monthsData[0].month} to ${monthsData[monthsData.length - 1].month}` : monthsData[0]?.month ?? monthFrom;
            downloadBlob(file, `TS ${monthPart} ${initials}${rfsPart ? ` - ${rfsPart}` : ''}.xlsx`);
            notify('GIZ timesheet downloaded.', 'success');
            onClose();
        } catch (error) {
            notify(`Couldn't create the timesheet: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const headerCheckbox = (key: 'inCountry' | 'perDiem' | 'accommodation', label: string) => (
        <Tooltip title="Tick for all days with expert-days">
            <FormControlLabel sx={{ margin: 0 }} labelPlacement="top"
                control={<Checkbox size="small" checked={allChecked(key)} indeterminate={!allChecked(key) && workedDays.some((d) => d[key])}
                    onChange={(e) => setForWorked({ [key]: e.target.checked } as Partial<GizDay>)} />}
                label={<Typography variant="caption" sx={{ lineHeight: 1.1 }}>{label}</Typography>} />
        </Tooltip>
    );

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth={step === 'review' ? 'lg' : 'sm'}>
            <DialogTitle>GIZ timesheet{draft && step === 'review' ? ` · ${draft.expertName} · ${monthsData.length > 1 ? `${monthShort(monthsData[0].month)} - ${monthShort(monthsData[monthsData.length - 1].month)}` : monthShort(month)}` : ''}</DialogTitle>
            <DialogContent dividers>
                {step === 'select' ? (
                    <Stack gap={2.5} sx={{ paddingTop: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Expert (timesheet of)</InputLabel>
                            <Select label="Expert (timesheet of)" value={userOid} onChange={(e) => setUserOid(e.target.value)}>
                                {userOptions.map((u) => <MenuItem key={u.oid} value={u.oid}>{u.name}{u.oid === me?.localAccountId ? ' (me)' : ''}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControlLabel sx={{ marginBottom: -1 }}
                            control={<Checkbox checked={multiMonth} onChange={(e) => setMultiMonth(e.target.checked)} />}
                            label="Print several months" />
                        <Box>
                            <Stack direction="row" gap={2} flexWrap="wrap">
                                <TextField type="month" label={multiMonth ? "From month" : "Month"} value={monthFrom} InputLabelProps={{ shrink: true }} sx={{ width: 200 }}
                                    onChange={(e) => { const v = e.target.value; setMonthFrom(v); if (v && (!monthTo || monthIndex(monthTo) < monthIndex(v))) setMonthTo(v); }} />
                                {multiMonth && (
                                    <TextField type="month" label="To month" value={monthTo} InputLabelProps={{ shrink: true }} sx={{ width: 200 }}
                                        error={!!rangeError} onChange={(e) => setMonthTo(e.target.value)} />
                                )}
                            </Stack>
                            {multiMonth && <FormHelperText error={!!rangeError}>{rangeError ?? `Full months only, up to ${MAX_MONTHS} months. One tab per month in the Excel file.`}</FormHelperText>}
                        </Box>
                        {noHours && (
                            <Alert severity="info">No implementation hours booked by {expertLabel} in {periodLabel}.</Alert>
                        )}
                        <Autocomplete options={projectOptions} value={selectedProject}
                            disabled={optionsLoading || projectOptions.length === 0}
                            loading={optionsLoading}
                            onChange={(_, value) => { setProjectId(value?.id ?? null); setRfsId(''); }}
                            getOptionLabel={(p) => `${p.projectNumber ?? ''} ${p.projectName ?? ''}${p.client ? ` · ${p.client}` : ''}`.trim()}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, p) => (
                                <li {...props} key={p.id}>
                                    <Box>
                                        <Typography variant="body2">{`${p.projectNumber ?? ''} ${p.projectName ?? ''}${p.client ? ` · ${p.client}` : ''}`.trim()}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatH(p.hours)}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Project"
                                    helperText={selectedProject ? `${formatH(selectedProject.hours)} implementation hours booked in ${periodLabel}` : undefined}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: <>{optionsLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>,
                                    }} />
                            )} />
                        {selectedProject?.rfsTracking && selectedProject.rfs.length === 0 && (
                            <Alert severity="info">None of these hours are booked on an RfS - the timesheet covers all hours on the project.</Alert>
                        )}
                        {selectedProject && selectedProject.rfs.length > 0 && (
                            <FormControl fullWidth disabled={optionsLoading}>
                                <InputLabel>RfS</InputLabel>
                                <Select label="RfS" value={rfsId} onChange={(e) => setRfsId(e.target.value as number | '')}>
                                    <MenuItem value="">All hours on the project (one timesheet) - {formatH(selectedProject.hours)}</MenuItem>
                                    {selectedProject.rfs.map((r) => <MenuItem key={r.id} value={r.id}>{rfsDisplay(r.name, r.code)} - {formatH(r.hours)}</MenuItem>)}
                                </Select>
                                {selectedProject.hoursWithoutRfs > 0 && (
                                    <FormHelperText>{formatH(selectedProject.hoursWithoutRfs)} are booked without an RfS and are only included in &quot;All hours on the project&quot;.</FormHelperText>
                                )}
                            </FormControl>
                        )}
                        <Box>
                            <FormControlLabel disabled={!rfsCode}
                                control={<Checkbox checked={includeRfsCode && !!rfsCode} onChange={(e) => setIncludeRfsCode(e.target.checked)} />}
                                label="Include RfS code in role/position (RfS code - Position)" />
                            {!rfsCode && <FormHelperText sx={{ marginTop: -0.5, marginLeft: 4 }}>Select an RfS that has a code to use this option.</FormHelperText>}
                            {multiMonth && <>
                            <Stack direction="row" alignItems="center">
                                <FormControlLabel disabled={!crossMonthAvailable} sx={{ marginRight: 0.5 }}
                                    control={<Checkbox checked={crossMonthAvailable && crossMonth} onChange={(e) => setCrossMonth(e.target.checked)} />}
                                    label="Aggregate half/full days across months" />
                                <GizAggregationHelp />
                            </Stack>
                            {!crossMonthAvailable && <FormHelperText sx={{ marginTop: -0.5, marginLeft: 4 }}>Needs more than one month and the &quot;Pool into half days&quot; rule.</FormHelperText>}
                            </>}
                        </Box>
                    </Stack>
                ) : draft && (
                    <>
                        {(!draft.project.contractNumber || !draft.project.gizProjectNumber) && (
                            <Alert severity="warning" sx={{ marginBottom: 2 }}>
                                The project has no {!draft.project.contractNumber ? 'contract number (VN)' : ''}{!draft.project.contractNumber && !draft.project.gizProjectNumber ? ' and no ' : ''}
                                {!draft.project.gizProjectNumber ? 'GIZ project number (PN)' : ''} - add it in the project's Overview tab, otherwise the header stays empty.
                            </Alert>
                        )}
                        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Name of expert" value={expertName} onChange={(e) => setExpertName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Role / position" value={role} onChange={(e) => setRole(e.target.value)}
                                    helperText={[
                                        includeRfsCode && draft.rfs?.code ? `Appears as: "${rolePreview}"` : null,
                                        draft.roleSource ? `Suggested from the cost plan: ${draft.roleSource}` : 'As in the GIZ contract, e.g. "Senior IT Expert"',
                                    ].filter(Boolean).join(' · ')} />
                            </Grid>
                        </Grid>

                        {monthsData.length > 1 && (
                            <Tabs value={Math.min(tab, monthsData.length - 1)} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ marginBottom: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                {monthsData.map((m) => <Tab key={m.month} label={monthShort(m.month)} />)}
                            </Tabs>
                        )}

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} sx={{ marginBottom: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                {current?.hoursBooked ?? 0} h project implementation on {draft.project.projectNumber ?? ''} {draft.project.projectName ?? ''}
                                {draft.rfs ? ` · RfS ${rfsDisplay(draft.rfs.name, draft.rfs.code)}` : ''} in {monthShort(month)}.
                            </Typography>
                            <Tooltip title={rule === 'pooled'
                                ? 'Hours are pooled over the month into half days (4 h = 0.5 day); the month total is hours / 8 rounded to the nearest 0.5. Descriptions move with their hours.'
                                : 'Each day on its own: 8 h or more = 1 day, 4 h or more = 0.5 day, less is not billable.'}>
                                <ToggleButtonGroup size="small" exclusive value={rule} disabled={busy}
                                    onChange={(_, value: GizRule | null) => { if (value && value !== rule) { setRule(value); loadDraft(value); } }}>
                                    <ToggleButton value="pooled">Pool into half days</ToggleButton>
                                    <ToggleButton value="daily">Per day</ToggleButton>
                                </ToggleButtonGroup>
                            </Tooltip>
                            <GizAggregationHelp />
                            <Box sx={{ flexGrow: 1 }} />
                            <TextField size="small" label="Location for all worked days" value={bulkLocation} onChange={(e) => setBulkLocation(e.target.value)} sx={{ width: 240 }} />
                            <Button size="small" variant="outlined" disabled={!bulkLocation.trim()} onClick={() => setForWorked({ location: bulkLocation.trim() })}>Apply</Button>
                        </Stack>

                        <Box sx={{ maxHeight: '52vh', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 70 }}>Date</TableCell>
                                        <TableCell sx={{ width: 60 }} align="right">Hours</TableCell>
                                        <TableCell sx={{ width: 100 }}>Expert-days</TableCell>
                                        <TableCell align="center" sx={{ width: 80 }}>{headerCheckbox('inCountry', 'In country')}</TableCell>
                                        <TableCell align="center" sx={{ width: 80 }}>{headerCheckbox('perDiem', 'Per diem')}</TableCell>
                                        <TableCell align="center" sx={{ width: 90 }}>{headerCheckbox('accommodation', 'Accommo-dation')}</TableCell>
                                        <TableCell sx={{ width: 160 }}>Location</TableCell>
                                        <TableCell>Activity / work content / output</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {days.map((d) => {
                                        const wd = weekday(month, d.day);
                                        const weekend = wd === 0 || wd === 6;
                                        return (
                                            <TableRow key={d.day} sx={{ backgroundColor: weekend ? alpha(theme.palette.text.primary, 0.03) : undefined }}>
                                                <TableCell sx={{ color: weekend ? 'text.disabled' : 'text.primary', whiteSpace: 'nowrap' }}>{WEEKDAYS[wd]} {d.day}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{d.hours ? d.hours : ''}</TableCell>
                                                <TableCell>
                                                    <Select size="small" variant="standard" value={d.expertDays ?? ''} displayEmpty sx={{ width: 70 }}
                                                        onChange={(e) => updateDay(d.day, { expertDays: e.target.value === '' ? null : Number(e.target.value) })}>
                                                        <MenuItem value="">–</MenuItem>
                                                        <MenuItem value={0.5}>0.5</MenuItem>
                                                        <MenuItem value={1}>1</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.inCountry} onChange={(e) => updateDay(d.day, { inCountry: e.target.checked })} /></TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.perDiem} onChange={(e) => updateDay(d.day, { perDiem: e.target.checked })} /></TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.accommodation} onChange={(e) => updateDay(d.day, { accommodation: e.target.checked })} /></TableCell>
                                                <TableCell><TextField variant="standard" size="small" fullWidth value={d.location} inputProps={{ maxLength: 100 }}
                                                    onChange={(e) => updateDay(d.day, { location: e.target.value })} /></TableCell>
                                                <TableCell><TextField variant="standard" size="small" fullWidth value={d.activity} inputProps={{ maxLength: 1000 }}
                                                    onChange={(e) => updateDay(d.day, { activity: e.target.value })} /></TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>

                        <Stack direction="row" gap={3} flexWrap="wrap" sx={{ marginTop: 1.5 }}>
                            <Typography variant="body2">{monthsData.length > 1 ? `${monthShort(month)}: ` : ''}<b>{totals.domestic + totals.abroad}</b> expert-days ({totals.domestic} domestic, {totals.abroad} in country)</Typography>
                            <Typography variant="body2">{totals.perDiem} per diem · {totals.accommodation} nights</Typography>
                        </Stack>
                        {monthsData.length > 1 && (
                            <Stack direction="row" gap={3} flexWrap="wrap" sx={{ marginTop: 0.5 }}>
                                <Typography variant="body2"><b>Total, all months: {grand.domestic + grand.abroad}</b> expert-days ({grand.domestic} domestic, {grand.abroad} in country) · {grandHours} h</Typography>
                                <Typography variant="body2">{grand.perDiem} per diem · {grand.accommodation} nights</Typography>
                            </Stack>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {step === 'review' && <Button startIcon={<ArrowBack />} onClick={() => setStep('select')} disabled={busy} sx={{ marginRight: 'auto' }}>Back</Button>}
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                {step === 'select' ? (
                    <Button variant="contained" disableElevation disabled={!projectId || !selectedProject || !rangeOk || busy || optionsLoading} onClick={() => loadDraft()}>
                        {busy ? <CircularProgress size={18} /> : 'Continue'}
                    </Button>
                ) : (
                    <Button variant="contained" disableElevation onClick={download} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <Download />}>
                        Download
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default TimesheetGizDialog;
